// src/utils/chat-history-api.js
// REST helpers for MongoDB-backed chat history. Each call attaches the Firebase
// ID token (same pattern as api-helpers.js). All endpoints are under /api/conversations.
import { auth } from '../firebase/firebaseConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const idToken = await user.getIdToken();
  return {
    Authorization: `Bearer ${idToken}`,
    'Content-Type': 'application/json'
  };
}

async function request(path, options = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}/api/conversations${path}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`chat-history ${options.method || 'GET'} ${path} failed: ${res.status} ${text}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// Returns [{ _id, title, lastMessageAt, createdAt }] newest-first.
export function listConversations() {
  return request('', { method: 'GET' });
}

// Returns { _id, title, messages: [...] }.
export function getConversation(id) {
  return request(`/${id}`, { method: 'GET' });
}

// Returns the new conversation stub { _id, title, lastMessageAt }.
export function createConversation(title = 'New chat') {
  return request('', { method: 'POST', body: JSON.stringify({ title }) });
}

export function renameConversation(id, title) {
  return request(`/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) });
}

export function deleteConversation(id) {
  return request(`/${id}`, { method: 'DELETE' });
}

// role: 'user' | 'assistant' | 'system'
export function appendMessage(id, role, content, payload) {
  return request(`/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify({ role, content, payload })
  });
}

// Ask the backend to LLM-generate a concise title. Returns { title }.
export function generateTitle(id, query) {
  return request(`/${id}/generate-title`, {
    method: 'POST',
    body: JSON.stringify({ query })
  });
}

export { API_BASE_URL };
